import { createSelector } from '@reduxjs/toolkit';

import { RootState } from '../../../../../init-store';
import {
    selectHasAdminsCalendarSummarizationReceivePermission,
    selectHasAdminsSummarizationReceivePermission,
    selectHasMembersCalendarSummarizationReceivePermission,
    selectHasMembersSummarizationReceivePermission,
    selectHasOwnerCalendarSummarizationReceivePermission,
    selectHasOwnerSummarizationReceivePermission,
} from '../permissions';

export const selectIsSummarizationAvailable = (state: RootState) =>
    Boolean(state.call.conference.config?.client_configuration?.summarization_available);

export const selectSummarizationStatus = (state: RootState) => state.call.summarization.summarizationStatus;

export const selectIsSummarizationRunning = (state: RootState) => selectSummarizationStatus(state) === 'RUNNING';

export const selectSummarizationModalId = (state: RootState) => state.call.summarization.summarizationModalId;

export const selectIsSummarizationMailForParticipants = (state: RootState) =>
    state.call.summarization.isSummarizationMailForParticipants;

/*
    Селектор возвращает ключ сообщения танкера, которое надо показать в модалке завершения саммаризации.
    Таких сообщений всего 48. Сообщение выбирается в зависимости от пермиссий рассылки саммаризации.
    Ключи составлены по шаблону: "stop-dialog-content(_<role>_<permission-state>)*"
    , где
        role - admin|owners|memebers
        permission-state - sr|csr|bsr
        , где
            sr - для роли включена пермиссия рассылки саммаризации по присутствию во время саммаризации
            csr - для роли включена пермиссия рассылки саммаризации если есть приглашение в календаре
            bsr - обе пермиссии (sr и csr) включены. У роли owner значения bsr и csr по сути сводятся
                  к одному и тому же сообщению в модалке. Поэтому значения bsr для этой роли нет.
                  Есть только csr
*/
export const selectStopDialogContentKey = createSelector(
    [
        selectHasMembersSummarizationReceivePermission,
        selectHasAdminsSummarizationReceivePermission,
        selectHasOwnerSummarizationReceivePermission,
        selectHasMembersCalendarSummarizationReceivePermission,
        selectHasAdminsCalendarSummarizationReceivePermission,
        selectHasOwnerCalendarSummarizationReceivePermission,
    ],
    (membersSR, adminsSR, ownerSR, membersCSR, adminsCSR, ownerCSR) => {
        if ([membersSR, adminsSR, ownerSR, membersCSR, adminsCSR, ownerCSR].some(Boolean)) {
            const [membersKeyPart, adminsKeyPart, ownerKeyPart] = [
                ['members', membersSR, membersCSR],
                ['admins', adminsSR, adminsCSR],
                ['owner', ownerSR, ownerCSR],
            ].map(([role, sr, csr]) => {
                if (role !== 'owner' && sr && csr) {
                    return `_${role}_bsr`;
                }
                if (csr) {
                    return `_${role}_csr`;
                }
                if (sr) {
                    return `_${role}_sr`;
                }
                return '';
            });
            return `stop-dialog-content${ownerKeyPart}${adminsKeyPart}${membersKeyPart}`;
        }
        return 'stop-dialog-content_nobody-receive';
    }
);
